import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action, setProperties } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import type { ReportActionMenuItem } from 'donor-elf-web/components/report-actions';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import AdminFund from 'donor-elf-web/models/admin/fund';
import AdminUser from 'donor-elf-web/models/admin/user';
import AdminUserFund from 'donor-elf-web/models/admin/user-fund';
import AdminUserLastOpenedEmail from 'donor-elf-web/models/admin/user-last-opened-email';
import AbilitiesService from 'donor-elf-web/services/abilities';
import CurrentUser from 'donor-elf-web/services/current-user';
import CachedDataset from 'donor-elf-web/services/cached-dataset';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import FundsService from 'donor-elf-web/services/funds-service';
import Settings from 'donor-elf-web/services/settings';
import { storageFor } from 'ember-local-storage';
import FileSaver from 'file-saver';
import Papa from 'papaparse';

interface UserItem {
  user: AdminUser;
  funds?: AdminFund[];
  lastOpenedEmailAt?: Date;
}

export default class AdminUsersIndexController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare cachedDataset: CachedDataset;
  @service declare currentUser: CurrentUser;
  @service declare formatDateService: FormatDateService;
  @service declare fundsService: FundsService;
  @service declare powerMenu: any;
  @service declare router: RouterService;
  @service declare settings: Settings;
  @service declare store: Store;
  @service declare storeCache: any;
  @storageFor('local-settings') localSettings: any;

  queryParams = ['q', 'showFunds', 'page'];
  @tracked q?: string = undefined;
  @tracked showFunds = 'false';
  @tracked selectedIndex = 0;
  @tracked page = 1;
  perPage = 50;

  @tracked loading = true;
  @tracked users: AdminUser[] = [];
  @tracked userLastOpenedEmails: {
    [userId: string]: AdminUserLastOpenedEmail;
  } = {};
  @tracked fundsById: { [fundId: string]: AdminFund } = {};
  @tracked userFundsByUserId: { [userId: string]: AdminUserFund[] } = {};

  @cached
  get items(): UserItem[] {
    let { userLastOpenedEmails, fundsById, userFundsByUserId } = this;
    return this.users.map((user) => {
      return {
        user,
        lastOpenedEmailAt: userLastOpenedEmails[user.id]?.lastOpenedAt,
        funds: userFundsByUserId[user.id]?.map(
          (userFund) => fundsById[userFund.fundId],
        ),
      } as UserItem;
    });
  }

  async load() {
    this.users = await this.cachedDataset.forModel('admin/user').load();

    if (this.showLastEmail) {
      this.loadLastOpenedEmails();
    }
    if (this.showFunds === 'true') {
      this.loadFundPermissions();
    }

    this.loading = false;
  }

  async loadLastOpenedEmails() {
    this.userLastOpenedEmails = await this.storeCache.indexSingleBy(
      'admin/user-last-opened-email',
      'userId',
    );
  }

  async loadFundPermissions() {
    this.fundsById = await this.storeCache.indexSingleBy('admin/fund', 'id');
    this.userFundsByUserId = await this.storeCache.indexMultipleBy(
      'admin/user-fund',
      'userId',
    );
  }

  get showLastLogin() {
    return this.settings.showUserLastLogin;
  }

  get showLastEmail() {
    return this.showLastLogin && this.currentUser.organizationId !== '403'; // East West can't see last emails because they're not in the shared environment. This should be refactored later
  }

  get sortByProperty() {
    return this.localSettings.get('adminUsersIndexSortByProperty');
  }
  set sortByProperty(val) {
    this.localSettings.set('adminUsersIndexSortByProperty', val);
  }

  get sortByDirection() {
    return this.localSettings.get('adminUsersIndexSortByDirection');
  }
  set sortByDirection(val) {
    this.localSettings.set('adminUsersIndexSortByDirection', val);
  }

  get itemsSortBy() {
    return [`${this.sortByProperty}:${this.sortByDirection}`];
  }

  @cached
  get sortedItems() {
    return sortByProperties<UserItem>(this.items, this.itemsSortBy);
  }

  @cached
  get filteredItems() {
    const sortedItems = this.sortedItems;
    let q = this.q;

    if (!q || q!.length < 2) {
      return sortedItems;
    }

    q = q.toLowerCase();

    return sortedItems.filter((item) => {
      return item.user.nameAndEmailLowerCase.indexOf(q) >= 0;
    });
  }

  get pagedItems() {
    let offset = (this.page - 1) * this.perPage;
    return this.filteredItems.slice(offset, offset + this.perPage);
  }

  get totalPages() {
    return Math.ceil(this.filteredItems.length / this.perPage);
  }

  get billableUsersCount() {
    return this.items.filter((x) => x.user.billable === true).length;
  }

  @action
  async templateDidInsert() {
    await this.load();
    // letting ReportAction component prepend its powerMenu options first
    setTimeout(() => {
      this.powerMenu.prependOptionsFn('admin-users', this.powerMenuOptions);
    });
  }

  powerMenuOptions() {
    return [
      {
        label: 'Add User',
        route: 'admin.users.add',
      },
    ];
  }

  @action
  templateWillDestroy() {
    this.powerMenu.removeOptions('admin-users');
    this.q = undefined;
    this.selectedIndex = 0;
    this.page = 1;
    this.loading = true;
  }

  get actionMenuItems(): ReportActionMenuItem[] {
    let showFunds = this.showFunds === 'true';
    return [
      {
        label: showFunds ? 'Hide Fund Permissions' : 'Show Fund Permissions',
        svgJar: 'check',
        onClick: () => {
          this.toggleShowFunds();
        },
      },
      {
        label: 'Download',
        svgJar: 'download',
        onClick: () => {
          this.download();
        },
      },
    ];
  }

  @action
  sortBy(property: string) {
    let newSortByDirection = 'asc';
    if (this.sortByProperty === property && this.sortByDirection === 'asc') {
      newSortByDirection = 'desc';
    }
    setProperties(this, {
      sortByProperty: property,
      sortByDirection: newSortByDirection,
      page: 1,
      selectedIndex: 0,
    });
  }

  @action
  handleKeyDown(e: any) {
    if (e.which === 40) {
      // Down arrow
      e.preventDefault();
      if (this.selectedIndex + 1 < this.filteredItems.length) {
        this.incrementProperty('selectedIndex');
      }
    } else if (e.which === 38) {
      // Up arrow
      e.preventDefault();
      if (this.selectedIndex > 0) {
        this.decrementProperty('selectedIndex');
      }
    }
  }

  @action
  handleSearchInput(e: any) {
    setProperties(this, {
      q: e.target.value,
      selectedIndex: 0,
    });
  }

  @action
  handleSearchEnter(e: any) {
    if (e.target.value !== (this.q || '')) {
      this.handleSearchInput(e);
    }
    let selectedItem = this.filteredItems[this.selectedIndex];
    if (selectedItem) {
      // if cmd+enter then immediately go to the user's selected fund
      if (e.metaKey && selectedItem.user.selectedFundId) {
        this.fundsService.viewFund(selectedItem.user.selectedFundId);
      } else {
        this.router.transitionTo('admin.users.show', selectedItem.user.id);
      }
    }
  }

  @action
  toggleShowFunds() {
    if (this.showFunds === 'true') {
      this.showFunds = 'false';
    } else {
      this.showFunds = 'true';
    }
  }

  @action
  changePage(pageNumber: number) {
    this.page = pageNumber;
    this.selectedIndex = 0;
  }

  @action
  download() {
    let formatDateService = this.formatDateService;
    let showFunds = this.showFunds === 'true';
    const { showLastLogin, showLastEmail } = this;
    let rows: [(string | null)[]] = [['Name', 'Email', 'Role', 'Billable', 'Created']];
    if (showFunds) {
      rows[0].push('Fund Permissions');
    }
    if (showLastLogin) {
      rows[0].push('Last Login');
    }
    if (showLastEmail) {
      rows[0].push('Last Opened Email');
    }
    this.sortedItems.forEach((item) => {
      let user = item.user;
      let row = [
        `${user.lastName}, ${user.firstName}`,
        user.email,
        user.role,
        user.billable ? 'Yes' : 'No',
        formatDateService.formatForFile(user.createdAt),
      ];
      if (showFunds) {
        row.push((item.funds?.map((fund) => fund.name) || []).join(', '));
      }
      if (showLastLogin) {
        row.push(formatDateService.formatForFile(user.currentSignInAt));
      }
      if (showLastEmail) {
        row.push(formatDateService.formatForFile(item.lastOpenedEmailAt));
      }
      rows.push(row);
    });
    let csv = Papa.unparse(rows);
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, 'users.csv', true);
  }
}
